import { useState, useRef } from "react";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { uploadAPI } from "../lib/api";

const ImageUploader = ({ 
    value, 
    onChange, 
    label = "Upload Image",
    accept = "image/*",
    maxSize = 10, // MB
    className = ""
}) => {
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState(value || "");
    const inputRef = useRef(null);

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file size
        if (file.size > maxSize * 1024 * 1024) {
            toast.error(`File terlalu besar. Maksimal ${maxSize}MB`);
            return;
        }

        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast.error("Hanya file gambar yang diperbolehkan");
            return;
        }

        setUploading(true);
        
        // Show preview immediately
        const localPreview = URL.createObjectURL(file);
        setPreview(localPreview);

        try {
            const response = await uploadAPI.upload(file);
            // Use full backend URL for the proxy path
            const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
            const uploadedUrl = `${backendUrl}${response.data.url}`;
            
            setPreview(uploadedUrl);
            onChange(uploadedUrl);
            toast.success("Gambar berhasil diupload");
        } catch (error) {
            console.error("Upload error:", error);
            toast.error("Gagal upload gambar");
            setPreview(value || "");
        } finally {
            setUploading(false);
            // Reset input
            if (inputRef.current) {
                inputRef.current.value = "";
            }
        }
    };

    const handleRemove = () => {
        setPreview("");
        onChange("");
        if (inputRef.current) {
            inputRef.current.value = "";
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (file) {
            // Create a synthetic event
            const syntheticEvent = {
                target: { files: [file] }
            };
            handleFileChange(syntheticEvent);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    return (
        <div className={className}>
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                onChange={handleFileChange}
                className="hidden"
                id={`upload-${label.replace(/\s/g, '-')}`}
                disabled={uploading}
            />
            
            {preview ? (
                <div className="relative group">
                    <div className="w-full aspect-video rounded-xl overflow-hidden bg-gray-100 border border-gray-200">
                        <img 
                            src={preview} 
                            alt={label} 
                            className="w-full h-full object-cover"
                        />
                    </div>
                    {!uploading && (
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
                            <label
                                htmlFor={`upload-${label.replace(/\s/g, '-')}`}
                                className="cursor-pointer px-3 py-2 bg-white rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
                            >
                                Ganti
                            </label>
                            <button
                                type="button"
                                onClick={handleRemove}
                                className="px-3 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                    {uploading && (
                        <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center">
                            <Loader2 className="w-8 h-8 text-white animate-spin" />
                        </div>
                    )}
                </div>
            ) : (
                <label
                    htmlFor={`upload-${label.replace(/\s/g, '-')}`}
                    className={`flex flex-col items-center justify-center w-full aspect-video rounded-xl border-2 border-dashed cursor-pointer transition-all ${
                        uploading 
                            ? 'border-red-300 bg-red-50' 
                            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                    }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                >
                    {uploading ? (
                        <Loader2 className="w-8 h-8 text-red-500 animate-spin mb-2" />
                    ) : (
                        <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                    )}
                    <span className="text-sm text-gray-500">
                        {uploading ? 'Mengupload...' : 'Klik atau drag untuk upload'}
                    </span>
                    <span className="text-xs text-gray-400 mt-1">
                        PNG, JPG hingga {maxSize}MB
                    </span>
                </label>
            )}
        </div>
    );
};

export default ImageUploader;
